import { gcd } from './arithmetic';
import { isNumber } from './coercion';
import { getPrecision } from './rounding';
import type { Fraction, Numeric } from './types';
import { asFraction, isFraction } from './types';

export function setDenominator(f: Numeric, denominator: number): Fraction {
  const f1 = asFraction(f);
  const mod = denominator / f1[1];
  const f2 = [mod * f1[0], mod * f1[1]] as const;
  if (getPrecision(f2[0]) < 1) {
    return f2;
  } else {
    return f1;
  }
}

export function fractionAdd(f1: Numeric, f2: Numeric) {
  f1 = asFraction(f1);
  f2 = asFraction(f2);
  const d = f1[1] * f2[1];
  return fractionSimplify([f2[1] * f1[0] + f1[1] * f2[0], d]);
}

const NEGATE = -1;

export function fractionSubtract(f1: Numeric, f2: Numeric) {
  f1 = asFraction(f1);
  f2 = asFraction(f2);
  return fractionAdd(f1, [NEGATE * f2[0], f2[1]]);
}

export function fractionMultiply(f1: Numeric, f2: Numeric) {
  f1 = asFraction(f1);
  f2 = asFraction(f2);
  return fractionSimplify([f1[0] * f2[0], f1[1] * f2[1]]);
}

export function fractionDivide(f1: Numeric, f2: Numeric) {
  f1 = asFraction(f1);
  f2 = asFraction(f2);
  return fractionSimplify([f1[0] * f2[1], f1[1] * f2[0]]);
}

export function fractionEquals(f1: Numeric, f2: Numeric) {
  f1 = asFraction(f1);
  f2 = asFraction(f2);
  return f1[0] * f2[1] == f1[1] * f2[0];
}

const DEFAULT_PRECISION = 10;
const PRECISION_BASE = 0.1;

export function fractionFuzzyEquals(
  f1: Fraction,
  f2: Fraction,
  precision = DEFAULT_PRECISION,
) {
  const v1 = f1[0] * f2[1];
  const v2 = f1[1] * f2[0];
  return Math.abs(v1 - v2) < Math.pow(PRECISION_BASE, precision);
}

export function fractionSimplify(f: Numeric) {
  f = asFraction(f);
  const d = Math.abs(gcd(f[0], f[1]));
  if (d == 0) return f;
  let answer = [f[0] / d, f[1] / d] as const;
  if (answer[1] < 0) answer = [answer[0] * NEGATE, answer[1] * NEGATE];
  return answer;
}

/**
 * Convert a fraction or number to its decimal representation.
 */
export function asDecimal(value: Numeric): number {
  if (isFraction(value)) return value[0] / value[1];
  return value;
}

/**
 * Convert to a fraction if the value is rational with a denominator ≤ maxDenominator,
 * otherwise return the original number.
 */
export function toFractionIfRational(
  n: Numeric | null | undefined,
  maxDenominator: number,
): Fraction | number {
  if (n == null) return 0;
  if (equals(0, n)) return 0;
  if (isFraction(n)) return fractionSimplify(asFraction(n));
  if (isNumber(n)) {
    for (let d = 1; d < maxDenominator; d++) {
      if (getPrecision(n * d) < 1) {
        return fractionSimplify([n * d, d]);
      }
    }
    return n;
  }
  return 0;
}

/**
 * Convert to fraction if rational (denominator ≤ 1000), else return number.
 */
const MAX_RATIONAL_DENOMINATOR = 1000;

export function fixNumber(n: Numeric | null | undefined): Fraction | number {
  if (n == null) return 0;
  return toFractionIfRational(n, MAX_RATIONAL_DENOMINATOR);
}

// Internal helper used by toFractionIfRational
function equals(v1: Numeric, v2: Numeric): boolean {
  return fractionEquals(asFraction(v1), asFraction(v2));
}
